import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import {
  CREATE_TRAVEL_REQUEST,
  GET_TRAVEL_REQUEST,
  UPDATE_TRAVEL_REQUEST,
  GET_TRAVEL_REQUEST_APPROVALS,
  CREATE_TRAVEL_APPROVER_SETTINGS,
  POST_TRAVEL_REQUEST_REASSIGN,
  GET_HSE_SECURITY_RISK,
  GET_TRAVEL_REQUEST_QUEST_CERTIFICATES,
  POST_ADD_TRAVEL_APPROVER,
  POST_REMOVE_TRAVEL_APPROVER,
  GET_TRAVEL_REQUEST_WITH_ID,
  POST_SEND_TRAVEL_DETAILS,
  GET_COUNTRY_INFORMATION,
  GET_COUNTRY_INFORMATION_DETAILS,
  GET_TRAVEL_REQUEST_COVID19RISK,
  GET_TRAVEL_REQUEST_DETAILS,
  GET_TRAVEL_REQUEST_MEDICALRISK
} from '../constants/apis';
import {
  ITravelRequestDetails,
  ITravelRequestResult,
  ITravelRequestUpdate,
  ITravelRequestReassign,
  ITravelRequestAddApprover,
  ITravelSendDetails,
  ITravelRequestDetailsQuery,
  ICreateTravelRequestInformation,
  ITravelRequestPayload
} from '../models/travel-request';
import { ITravelRequestPostResponse, ITravelRequestUpdatePostResponse, ITravelRequestDetailsResponse, ITravelRequestApprovalSettingsResult, ITravelRequestCovidRiskResponse, ITravelRequestQuestCertificatesResponse, ITravelRequestMedicalRiskResponse } from '../models/api';
import { ITravelRequestApproverSettings } from '../models/travel-settings';
import { ICountrySecurityRiskResult } from '../models/security-risk';
import { IGetCountryInformationResult, IGetCountryInformationDetailsResult } from '../models/country-information';

@Injectable({
  providedIn: 'root'
})
export class TravelService {

  travelRequestFilter: BehaviorSubject<number>;
  travelRequestApprovalsFilter: BehaviorSubject<number>;

  travelRequestDetails: BehaviorSubject<ICreateTravelRequestInformation | undefined>;

  constructor(private httpClient: HttpClient) {
    this.travelRequestFilter = new BehaviorSubject<number>(0);
    this.travelRequestApprovalsFilter = new BehaviorSubject<number>(0);
    this.travelRequestDetails = new BehaviorSubject<ICreateTravelRequestInformation| undefined>(undefined);
  }

  postTravelRequest(data: ITravelRequestDetails): Observable<ITravelRequestPostResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<ITravelRequestPostResponse>(CREATE_TRAVEL_REQUEST, data, options);
  }

  getTravelRequest(data: ITravelRequestPayload): Observable<ITravelRequestResult> {
    return this.httpClient.post<ITravelRequestResult>(GET_TRAVEL_REQUEST, data);
  }

  getTravelRequestApprovals(data: ITravelRequestPayload): Observable<ITravelRequestResult> {
    return this.httpClient.post<ITravelRequestResult>(GET_TRAVEL_REQUEST_APPROVALS, data);
  }

  updateTravelRequest(data: ITravelRequestUpdate): Observable<ITravelRequestUpdatePostResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<ITravelRequestPostResponse>(UPDATE_TRAVEL_REQUEST, data, options);
  }

  postTravelApproverSettings(data: ITravelRequestApproverSettings): Observable<ITravelRequestPostResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<ITravelRequestPostResponse>(CREATE_TRAVEL_APPROVER_SETTINGS, data, options)
  }

  getTravelRequestApproverSettings(): Observable<ITravelRequestApprovalSettingsResult> {
    return this.httpClient.get<ITravelRequestApprovalSettingsResult>(CREATE_TRAVEL_APPROVER_SETTINGS);
  }

  postTravelRequestReassignDetails(data: ITravelRequestReassign): Observable<ITravelRequestPostResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<ITravelRequestPostResponse>(POST_TRAVEL_REQUEST_REASSIGN, data, options)
  }

  getHSERiskInformation(originCountry: string, destinationCountry: string): Observable<ICountrySecurityRiskResult> {
    return this.httpClient.get(GET_HSE_SECURITY_RISK + 'sourceCountry=' + originCountry + '&destinationCountry=' + destinationCountry);
  }

  getTravelRequestFilter(): Observable<number> {
    return this.travelRequestFilter.asObservable();
  }

  setTravelRequestFilter(value: number): void {
    this.travelRequestFilter.next(value);
  }

  getTravelRequestApprovalsFilter(): Observable<number> {
    return this.travelRequestApprovalsFilter.asObservable();
  }

  setTravelRequestApprovalsFilter(value: number): void {
    this.travelRequestApprovalsFilter.next(value);
  }

  getTravelRequestCovid19Risk(country: string, state?: string): Observable<ITravelRequestCovidRiskResponse> {
    if(state){
      return this.httpClient.get<ITravelRequestCovidRiskResponse>(GET_TRAVEL_REQUEST_COVID19RISK + `${country}&state=${state}`);
    }
    return this.httpClient.get<ITravelRequestCovidRiskResponse>(GET_TRAVEL_REQUEST_COVID19RISK + country);
  }

  getTravelRequestMedicalRisk(country: string): Observable<ITravelRequestMedicalRiskResponse> {
    return this.httpClient.get<ITravelRequestMedicalRiskResponse>(GET_TRAVEL_REQUEST_MEDICALRISK + country);
  }

  getTravelRequestQuestCertficates(country: string, gin: string): Observable<ITravelRequestQuestCertificatesResponse> {
    let params = new HttpParams();
    params = params.append('country', country);
    params = params.append('gin', gin);
    return this.httpClient.get<ITravelRequestQuestCertificatesResponse>(GET_TRAVEL_REQUEST_QUEST_CERTIFICATES, { params: params });
  }

  postAddApprover(approver: ITravelRequestAddApprover): Observable<ITravelRequestPostResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<ITravelRequestPostResponse>(POST_ADD_TRAVEL_APPROVER, approver, options);
  }

  postRemoveApprover(approver: number[]): Observable<ITravelRequestPostResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<ITravelRequestPostResponse>(POST_REMOVE_TRAVEL_APPROVER, approver, options);
  }

  getTripDetails(queryData: ITravelRequestDetailsQuery) {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<any>(GET_TRAVEL_REQUEST_WITH_ID, queryData, options);
  }

  postSendTravelDetails(details: ITravelSendDetails): Observable<ITravelRequestPostResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    }
    return this.httpClient.post<ITravelRequestPostResponse>(POST_SEND_TRAVEL_DETAILS, details, options)
  }

  getCountryInformation(): Observable<IGetCountryInformationResult> {
    return this.httpClient.get<IGetCountryInformationResult>(GET_COUNTRY_INFORMATION);
  }

  getCountryInformationDetails(country: string): Observable<IGetCountryInformationDetailsResult> {
    return this.httpClient.get<IGetCountryInformationDetailsResult>(GET_COUNTRY_INFORMATION_DETAILS + country);
  }


  getTravelRequestInformation = () => {
    return this.travelRequestDetails.asObservable();
  }

  setTravelRequestInformation = (value: ICreateTravelRequestInformation | undefined) => {
    this.travelRequestDetails.next(value);
  }

  getTravelRequestDetails = (id: number) => {
    return this.httpClient.get<ITravelRequestDetailsResponse>(GET_TRAVEL_REQUEST_DETAILS + id)
  }
}
